/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState, useRef } from "react";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import {
  Row,
  Col,
  Box,
  Text,
  Spacer,
  theme,
  Hidden,
  Visible,
  FlexContainer,
} from "@nordcloud/gnui";
import {
  useEnvironmentQuery,
  useEnvironmentContactsQuery,
  useNidEventsQuery,
  EnvironmentQuery,
  EnvironmentType,
  useUpdateEnvironmentMutation,
} from "~/generated/graphql";
import {
  BreadcrumbsBox,
  BudgetChart,
  BudgetChartWrapper,
  LoaderWrap,
  ErrorWrap,
  InlineText,
  InlineContacts,
  ResponsiveBrickLoader,
  TableLoader,
} from "~/components";
import { getApplicationStatus } from "~/components/ApplicationItem";
import {
  CostBox,
  CustomComponents,
  formatCostObject,
} from "~/components/CostBox";
import { formatSavingSuggestions } from "~/components/CostBox/helpers";
import { dateFormat } from "~/constants";
import {
  useCurrency,
  useUuidValidator,
  useQueryState,
  useScroll,
  useScrollDisclosure,
} from "~/hooks";
import { ResourceFilter } from "~/models";
import { ACTIONS, Can } from "~/services/auth";
import { periodDayJs, useBillingPeriod } from "~/services/customers";
import { isNotNil } from "~/tools";
import { DetailsTitle, EnvTypeSelect } from "~/views/environments/components";
import { EnvironmentCostTabs } from "~/views/environments/Details/EnvironmentCostTabs";
import { EnvironmentDiscoveryRules } from "../EnvironmentDiscoveryRules";
import { EnvironmentInput } from "../EnvironmentInput";
import { EnvironmentPopover } from "../EnvironmentPopover";
import { RouteParams } from "../types";
import { EnvironmentDetailsTabs } from "./EnvironmentDetailsTabs";

const TAGS = [
  {
    color: "success",
    text: "Environment",
  },
];

// TODO: Reduce amount of code
// eslint-disable-next-line max-lines-per-function
export function EnvironmentDetails() {
  const scrollToRef = useRef<HTMLDivElement>(null);
  const [isEstateRecordsLoading, setIsEstateRecordsLoading] = useState(false);
  const [isEstateRecordsClicked, setIsEstateRecordsClicked] = useState(false);

  const { application: appId, environment: envId } = useParams<RouteParams>();
  useUuidValidator(appId);
  useUuidValidator(envId);

  const {
    state: { waste },
  } = useQueryState<ResourceFilter & { tab?: string; query?: string }>();
  const { currency } = useCurrency();

  const { data: eventsData } = useNidEventsQuery({
    variables: {
      nid: envId,
      from: periodDayJs().startOf("month").toISOString(),
      to: periodDayJs().endOf("month").toISOString(),
    },
  });

  const { data, error, loading } = useEnvironmentQuery({
    variables: {
      id: envId,
      year: dayjs().format(dateFormat.year),
    },
  });

  const [updateEnvironment, { loading: isUpdateEnvironmentLoading }] =
    useUpdateEnvironmentMutation();

  const { set: setCostTab } = useQueryState<{ costTab: "budget" }>();

  const {
    nid,
    currentMonthCosts,
    forecast,
    application: envApplication,
    id,
    name,
  } = mapEnvironment(data?.environment);

  const breadcrumbLabels = data?.environment
    ? [
        { value: id, label: name },
        { value: appId, label: envApplication?.name },
      ]
    : undefined;

  const { isCurrentBillingPeriod } = useBillingPeriod();
  const costs = formatCostObject({
    data: {
      forecast: data?.environment?.cost?.forecast,
      budget: data?.environment?.budgetYearly?.budgetByMonth,
      cloudWasteCost: data?.environment?.cloudWasteAggregate?.cost,
      co2e: data?.environment?.co2e,
      currentMonth: data?.environment?.cost?.currentMonth,
      previousMonth: data?.environment?.cost?.previousMonth,
      co2eSavings: data?.environment?.cloudWasteAggregate?.co2e,
    },
    currency,
    isCurrentBillingPeriod,
  });

  const budget = costs.budget ?? 0;
  const hasBudget = budget > 0;
  const baseline = forecast <= 0 ? costs.currentMonth : forecast;
  const progress = hasBudget ? (100 * baseline) / budget : 0;
  const status = getApplicationStatus(progress);

  const {
    triggerScroll,
    scrollToRef: scrollToBudgetRef,
    isScrolled,
    setIsScrolled,
  } = useScrollDisclosure();

  useScroll({
    ref: scrollToBudgetRef,
    smooth: true,
    trigger: () => isScrolled,
    onFinish: () => {
      setIsScrolled(false);
      setCostTab("costTab")("budget");
    },
  });

  const savingSuggestions = formatSavingSuggestions({
    show: data?.environment?.cloudWasteAggregate?.hasWaste,
    type: "Environment",
    appId,
    envId,
    loading: isEstateRecordsLoading && waste === true,
    clicked: () => setIsEstateRecordsClicked(true),
  });

  useScroll({
    trigger: () =>
      isEstateRecordsClicked && !isEstateRecordsLoading && waste === true,
    ref: scrollToRef,
    once: true,
  });

  const handleEnvTypeChange = async (type: EnvironmentType | null) => {
    await updateEnvironment({
      variables: {
        nid: id,
        input: { type },
      },
    });
  };

  return (
    <Row>
      <Col>
        <LoaderWrap loading={loading} Component={Loader}>
          <BreadcrumbsBox
            tags={TAGS}
            labels={breadcrumbLabels}
            title={
              data?.environment ? `${envApplication.name} - ${name}` : envId
            }
          >
            <Can action={ACTIONS.updateDiscoveryRules}>
              <EnvironmentDiscoveryRules envId={envId} />
            </Can>
            <EnvironmentPopover
              name={name}
              nid={nid}
              applicationId={envApplication.nid}
            />
          </BreadcrumbsBox>
          <ErrorWrap error={error}>
            <Box spacing="spacing04">
              <Row>
                <Col xs={8} sm={8} md={5} lg={8}>
                  <FlexContainer css={{ height: "100%" }}>
                    <Hidden xs>
                      <BudgetChartWrapper>
                        <BudgetChart
                          progress={progress}
                          color={status.color}
                          isForecast={forecast > 0}
                          hasBudget={budget > 0}
                          icon="cloud"
                        />
                      </BudgetChartWrapper>
                    </Hidden>
                    <div>
                      <Text weight="medium">Environment Details</Text>
                      <FlexContainer alignItems="center">
                        <DetailsTitle>Environment Name:</DetailsTitle>
                        <InlineText
                          type="environment"
                          property="name"
                          query={useEnvironmentContactsQuery}
                          mutation={useUpdateEnvironmentMutation}
                          queryVariables={{
                            id: envId,
                          }}
                        />
                      </FlexContainer>
                      <Spacer height={theme.spacing.spacing02} />
                      <FlexContainer alignItems="center">
                        <DetailsTitle>Contact Person:</DetailsTitle>
                        <InlineContacts
                          type="environment"
                          property="ownerId"
                          query={useEnvironmentContactsQuery}
                          mutation={useUpdateEnvironmentMutation}
                          queryVariables={{
                            id: envId,
                          }}
                        />
                      </FlexContainer>
                      <Spacer height={theme.spacing.spacing02} />
                      <FlexContainer alignItems="center">
                        <DetailsTitle>Business Contexts:</DetailsTitle>
                        <EnvironmentInput environmentId={envId} />
                      </FlexContainer>
                      <Spacer height={theme.spacing.spacing02} />
                      <FlexContainer alignItems="center">
                        <DetailsTitle>Description:</DetailsTitle>
                        <InlineText
                          type="environment"
                          property="description"
                          query={useEnvironmentContactsQuery}
                          mutation={useUpdateEnvironmentMutation}
                          required={false}
                          queryVariables={{
                            id: envId,
                          }}
                        />
                      </FlexContainer>
                      <Spacer height={theme.spacing.spacing02} />
                      <FlexContainer alignItems="center">
                        <DetailsTitle>Environment Type:</DetailsTitle>
                        <EnvTypeSelect
                          type={data?.environment?.environmentType}
                          loading={isUpdateEnvironmentLoading}
                          onChange={handleEnvTypeChange}
                        />
                      </FlexContainer>
                    </div>
                  </FlexContainer>
                </Col>
                <Visible sm xs>
                  <Spacer height={theme.spacing.spacing04} width="100%" />
                </Visible>
                <Col sm={8} md={3} lg={4}>
                  <CostBox
                    numericalValues={costs}
                    customComponent={CustomComponents.BUDGET}
                    loading={loading}
                    savingSuggestions={savingSuggestions}
                    customComponentConfig={{
                      scrollToBudget: triggerScroll,
                    }}
                  />
                </Col>
              </Row>
            </Box>
            <div ref={scrollToBudgetRef}>
              <EnvironmentCostTabs
                events={eventsData?.events ?? []}
                currentMonthCosts={currentMonthCosts}
                environmentType={data?.environment?.environmentType}
                environmentName={data?.environment?.name}
                forecast={forecast}
              />
            </div>
            <Spacer height={theme.spacing.spacing04} width="100%" />
            <div ref={scrollToRef}>
              <EnvironmentDetailsTabs
                setEstateRecordsLoading={setIsEstateRecordsLoading}
              />
            </div>
          </ErrorWrap>
        </LoaderWrap>
      </Col>
    </Row>
  );
}

function mapEnvironment(environment: EnvironmentQuery["environment"]) {
  return {
    id: environment?.id ?? "",
    nid: environment?.nid ?? "",
    name: environment?.name ?? "",
    application: {
      nid: environment?.application?.nid ?? "",
      name: environment?.application?.name ?? "",
    },
    forecast: Number(environment?.cost?.forecast ?? "0"),
    currentMonthCosts:
      environment?.cost?.currentMonthDaily?.filter(isNotNil) ?? [],
    co2e: environment?.co2e ?? "",
  };
}

function Loader() {
  return (
    <>
      <ResponsiveBrickLoader
        height={90}
        css={{ marginBottom: theme.spacing.spacing04 }}
      />
      <ResponsiveBrickLoader
        height={310}
        css={{ marginBottom: theme.spacing.spacing04 }}
      />
      <ResponsiveBrickLoader
        height={560}
        css={{ marginBottom: theme.spacing.spacing04 }}
      />
      <ResponsiveBrickLoader height={200} />
      <TableLoader
        gapY={10}
        gapX={6}
        rows={[
          {
            count: 8,
            height: 60,
            items: [
              {
                widthPercent: 35,
              },
              {
                widthPercent: 45,
              },
              {
                widthPercent: 15,
              },
              {
                widthPercent: 5,
              },
            ],
          },
        ]}
      />
    </>
  );
}
